// CanvasCoordinator - Manages canvas visualization lifecycle
// All canvas entrypoints register with this singleton. It handles:
// - Turbo navigation events (turbo:load for init, turbo:before-cache for cleanup)
// - Deferred initialization (waits for modules to register via rAF)
// - One-shot factory calls (each factory runs once per navigation, not retried)
// - Cross-canvas interaction bus (disposed and replaced on each navigation)

import { CanvasBus } from './engine/bus'
import { DataCache } from './engine/data-cache'

class CanvasCoordinator {
  constructor() {
    this.canvases = new Map()
    this.bus = new CanvasBus()
    this.cache = new DataCache()
    this._rafId = null
    this._retryId = null

    // turbo:load fires once after the final page is ready.
    // NOT turbo:render — that fires for cached previews too,
    // causing init against stale DOM that gets replaced (flash-then-blank).
    document.addEventListener('turbo:load', () => {
      this._deferredInitialize()

      // RETRY WORKAROUND: blank canvases on first Turbo visit (e.g. login → portfolio)
      //
      // Problem: On the first Turbo visit to a page with canvas entrypoints, the
      // <script type="module"> tags are activated by Turbo but load asynchronously.
      // The rAF from _deferredInitialize() above fires before the modules have executed
      // their register() calls, so initialize() finds nothing to do. The modules then
      // register and call _deferredInitialize() themselves, which *should* work — but
      // the race is unreliable and sometimes produces blank canvases.
      //
      // Fix: retry initialization after 80ms, giving modules time to load and register.
      // Safe because initialize() skips already-initialized canvases (no double-init).
      //
      // This only matters for the FIRST Turbo visit (modules not yet loaded). On
      // subsequent visits, registrations persist and the rAF above handles everything.
      // If this retry is still insufficient, increase the delay or move entrypoint
      // imports into application.js (guaranteed to load before turbo:load).
      clearTimeout(this._retryId)
      this._retryId = setTimeout(() => this._deferredInitialize(), 80)
    })
    document.addEventListener('turbo:before-cache', () => this.cleanup())
  }

  /**
   * Register a canvas factory function.
   * Triggers deferred initialization so late-registering modules
   * (after turbo:load has already fired) still get initialized.
   */
  register(id, factory, options = {}) {
    this.canvases.set(id, {
      id,
      factory,
      priority: options.priority || 100,
      instance: null,
      initialized: false
    })
    this._deferredInitialize()
  }

  /**
   * Schedule initialization for the next animation frame.
   * cancelAnimationFrame deduplicates: only the last call wins,
   * so rapid register() calls batch into a single initialize().
   */
  _deferredInitialize() {
    cancelAnimationFrame(this._rafId)
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null
      this.initialize()
    })
  }

  /**
   * Initialize canvases that haven't been attempted yet.
   * Uses an `initialized` flag (not instance truthiness) so factories
   * that return null/undefined (canvas not on this page) aren't retried.
   * The flag resets on cleanup (turbo:before-cache), enabling re-init
   * on the next Turbo navigation.
   */
  initialize() {
    const pending = Array.from(this.canvases.values())
      .filter(c => !c.initialized)
      .sort((a, b) => a.priority - b.priority)

    for (const canvas of pending) {
      try {
        canvas.instance = canvas.factory()
      } catch (error) {
        console.error(`Error initializing canvas ${canvas.id}:`, error)
      }
      canvas.initialized = true
    }
  }

  /**
   * Destroy all canvas instances and reset initialized flags.
   * Called on turbo:before-cache so the next turbo:load re-creates
   * everything against fresh DOM.
   */
  cleanup() {
    cancelAnimationFrame(this._rafId)
    this._rafId = null
    clearTimeout(this._retryId)
    this._retryId = null

    this.bus.dispose()
    this.bus = new CanvasBus()
    this.cache.dispose()
    this.cache = new DataCache()

    for (const canvas of this.canvases.values()) {
      if (canvas.instance?.destroy) {
        try {
          canvas.instance.destroy()
        } catch (error) {
          console.error(`Error destroying canvas ${canvas.id}:`, error)
        }
      }
      canvas.instance = null
      canvas.initialized = false
    }
  }
}

// Create global singleton
if (!window.__canvasCoordinator) {
  window.__canvasCoordinator = new CanvasCoordinator()
}

export const coordinator = window.__canvasCoordinator
